import { autoinject } from "aurelia-framework";
import { Location } from "../models/location";
import { applicationLogger } from "../logging/logger";
import { LocationHub } from "./location-hub";
import { EventAggregator, Subscription } from "aurelia-event-aggregator";
import { HubEvents } from "../events/hub-events";
import { Store } from "aurelia-store";
import { Subscription as StateSubscription } from "rxjs";
import { IState } from "../store/state";
import { HubConnectionState } from "@microsoft/signalr";

@autoinject
export class HubManager {
	private locations: Array<Location>;
	private locationHubDictionary = new Map<string, LocationHub>();
	private readonly eventAggregator: EventAggregator;
	private readonly hubRefreshSubscription: Subscription;
	private readonly hubClosedSubscription: Subscription;
	private readonly store: Store<IState>;
	private stateSubscription: StateSubscription;
	private lastSync = 0;
	private syncInterval = 60000; // sync every minute

	constructor(eventAggregator: EventAggregator, store: Store<IState>) {
		this.eventAggregator = eventAggregator;
		this.configureNetworkDetection();
		this.store = store;

		this.hubRefreshSubscription = this.eventAggregator.subscribe(HubEvents.refresh,
			() => {
				this.refreshHubs();
			});

		this.hubClosedSubscription = this.eventAggregator.subscribe(HubEvents.signalRDisconnect, (locationName) => {
			if (this.locationHubDictionary.has(locationName)) {
				applicationLogger.debug(`Removing "${locationName}" hub`);
				const locationHub = this.locationHubDictionary.get(locationName);
				locationHub.dispose();
				this.locationHubDictionary.delete(locationName);
			} else {
				applicationLogger.debug(`Could not find "${locationName}" hub to remove it from the location dictionary.`);
			}
		});
	}

	start() {
		applicationLogger.info("HubManager started.");
		this.stateSubscription = this.store.state.subscribe((state) => {
			const locationState = Object.assign({}, state.locationState);
			this.initialize(locationState.selectedLocations);
		});
	}

	private configureNetworkDetection() {
		window.addEventListener("online", () => {
			this.createHubs();
			this.eventAggregator.publish(HubEvents.networkChange, true);
		});
		window.addEventListener("offline", () => {
			this.disposeHubs();
			this.eventAggregator.publish(HubEvents.networkChange, false);
		});
	}

	refreshHubs() {
		this.disposeHubs();
		setTimeout(() => {
			this.createHubs();
		}, 500);
	}

	disposeHubs() {
		applicationLogger.info(`Disposing SignalR hubs`);
		this.locationHubDictionary.forEach((locationHub: LocationHub, key: string) => {
			locationHub.dispose();
		});
		this.locationHubDictionary = new Map<string, LocationHub>();
	}

	initialize(locations: Location[]) {
		applicationLogger.info(`Initializing SignalR hubs`);
		this.locations = locations;
		locations.forEach((location) =>
		{
			if (!this.locationHubDictionary.has(location.id)) {
				this.locationHubDictionary.set(location.id, this.createHubConnection(location));
			}
		});

		// force the signalr hubs to be refreshed if the app has idled too long.
		setInterval(() => {
			const now = new Date().getTime();
			if ((now - this.lastSync) > this.syncInterval) {
				if (this.locationHubDictionary.size === 0) {
					applicationLogger.debug("Conditions met to recreate all location hubs.");
					this.lastSync = now;
					this.refreshHubs();
				} else {
					this.locationHubDictionary.forEach((locationHub: LocationHub, key: string) => {
						if (locationHub.hubConnection.state === HubConnectionState.Disconnected) {
							locationHub.start();
						}
					});
				}
			}
		}, 5000); // check every 5 seconds whether a minute has passed since last sync

		this.eventAggregator.publish(HubEvents.networkChange, true);
	}

	createHubs() {

		if (this.locations) {
			this.locations.forEach((location) => {
				applicationLogger.debug(`Creating SignalR hub for ${location.code} - ${location.id}`);
				this.locationHubDictionary.set(location.id, this.createHubConnection(location));
			});
		}
	}

	createHubConnection(location: Location): LocationHub {
		const locationHub = new LocationHub();
		locationHub.initialize(location, this.eventAggregator);
		return locationHub;
	}

	dispose() {
		this.stateSubscription.unsubscribe();
		this.hubRefreshSubscription.dispose();
		this.hubClosedSubscription.dispose();
		this.disposeHubs();
	}
}
